import { useMemo } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  IconButton,
  Tooltip
} from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { usePanelStyles } from '@/styles/utilities/hooks';
import MolstarViewer from '@/shared/components/viewer/MolstarViewer';
import { API_BASE_URL } from '@/shared/constants/config';

/**
 * FMOInputViewer Component
 * 
 * Displays a 3D molecular structure viewer for selected FMO inputs.
 * Matches the behavior and layout of ReviewPanel in StructuresTab.
 * 
 * @param {Object} props
 * @param {Object} props.fmoInput - The selected FMO input object to display
 * @param {boolean} props.isCollapsed - Whether the panel is collapsed
 * @param {Function} props.onCollapse - Callback to collapse the panel
 * @param {number} props.width - Panel width in pixels (density-aware)
 * @param {number} props.minWidth - Minimum panel width in pixels (density-aware)
 */
export default function FMOInputViewer({ 
  fmoInput,
  isCollapsed,
  onCollapse,
  width,
  minWidth
}) {
  const theme = useTheme();
  const panelStyles = usePanelStyles();
  
  // Generate PDB URL for the selected FMO input's complex
  const pdbUrl = useMemo(() => {
    if (!fmoInput?.complex_?.id_) return null;
    return `${API_BASE_URL}/complexes/${fmoInput.complex_.id_}/pdb`;
  }, [fmoInput]);
  
  // Use same width as left sidebar panels for consistency
  const panelWidth = width || theme.dimensions.panel.leftSidebarWidth;
  const panelMinWidth = minWidth || theme.dimensions.panel.leftSidebarMinWidth;
  
  if (isCollapsed) {
    return null;
  }
  
  return (
    <Paper sx={{ 
      width: panelWidth,
      minWidth: panelMinWidth,
      display: 'flex', 
      flexDirection: 'column',
      borderRadius: 0,
      borderLeft: '1px solid',
      borderColor: 'divider'
    }}>
      {/* Header */}
      <Box sx={{ 
        p: theme.spacing(1.5), 
        height: theme.dimensions.panel.headerHeight,
        borderBottom: '1px solid', 
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: theme.palette.grey[50]
      }}>
        <Typography sx={panelStyles.headerTitle}>
          FMO Input Viewer{fmoInput?.id_ ? ` #${fmoInput.id_}` : ''}
        </Typography>
        <Tooltip title="Collapse panel">
          <IconButton 
            size="small" 
            onClick={onCollapse}
            sx={{ padding: theme.spacing(0.5) }}
          >
            <ChevronRight sx={{ fontSize: theme.dimensions.icon.small }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {(pdbUrl || fmoInput) ? (
          pdbUrl ? (
            <MolstarViewer pdbUrl={pdbUrl} />
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              p: theme.spacing(2)
            }}>
              <Typography color="text.secondary" align="center" sx={{ fontSize: theme.typography.fontSize.base }}>
                No structure available for this FMO input
              </Typography>
            </Box>
          )
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            p: theme.spacing(2)
          }}>
            <Typography color="text.secondary" align="center" sx={{ fontSize: theme.typography.fontSize.base }}>
              Select an FMO input from the table to view it in 3D
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}